import {Component, Input, OnDestroy} from '@angular/core';
import {AbstractControl, FormControl, ReactiveFormsModule, ValidationErrors} from '@angular/forms';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {OrganisationsService} from '@shared/api/services/v2/organisations.service';
import {isAlreadyNotified} from '@shared/api/services/v2/api-request';
import {ORGANISATION_REASON_MAX_LENGTH} from '@shared/api/models/command/organisation.model';

function reasonMaxLength(control: AbstractControl<string | null>): ValidationErrors | null {
    const length = (control.value ?? '').trim().length;
    return length > ORGANISATION_REASON_MAX_LENGTH
        ? {maxlength: {max: ORGANISATION_REASON_MAX_LENGTH, actual: length}}
        : null;
}

/**
 * UNIVAPLAT-3693: delete-organisation modal with an optional reason.
 *
 * Opened by the delete panel via NgbModal (`ariaLabelledBy: 'delete-organisation-title'`), which
 * supplies role=dialog, focus trap, Escape and focus return to the opener. Closes with `true` once
 * the organisation is deleted; Cancel, the close icon and Escape dismiss without a call. On an error
 * the modal stays open and the backend text is toasted verbatim (422 organisation_linked_to_contracts,
 * organisation_linked_to_services, organisation_read_only). The reason is personal data: never
 * logged, dropped on destroy.
 */
@Component({
    selector: 'cdss-delete-organisation-modal',
    standalone: true,
    imports: [ReactiveFormsModule],
    templateUrl: './delete-organisation.modal.html'
})
export class DeleteOrganisationModal implements OnDestroy {
    @Input() organisationId!: number;

    readonly maxLength = ORGANISATION_REASON_MAX_LENGTH;
    readonly reason = new FormControl<string>('', {nonNullable: true, validators: [reasonMaxLength]});
    saving = false;

    constructor(
        public modal: NgbActiveModal,
        private organisationsService: OrganisationsService,
        private toastr: ToastrService,
    ) {
    }

    get length(): number {
        return this.reason.value.trim().length;
    }

    ngOnDestroy(): void {
        this.reason.reset('');
    }

    async submit(): Promise<void> {
        if (this.saving || this.reason.invalid) {
            return;
        }
        this.saving = true;
        try {
            const reason = this.reason.value.trim();
            await this.organisationsService.delete(this.organisationId, reason !== '' ? reason : undefined);
            this.modal.close(true);
        } catch (error: any) {
            if (!isAlreadyNotified(error)) {
                this.toastr.error(error?.error?.error || error?.error?.message || 'Something went wrong. Please try again.');
            }
        } finally {
            this.saving = false;
        }
    }
}
